// Small sound effects made with Web Audio (no audio files): a soft tick when the turn moves, a
// two-note chime when it is your turn and a fanfare for the tournament winner. Browsers only allow
// sound after the user has interacted with the page, so the audio context is unlocked on the first
// tap or key press; before that, sounds are silently skipped.
import { useQueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import { useSession } from './session.tsx';

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  ctx ??= new AudioContext();
  return ctx;
}

if (typeof window !== 'undefined') {
  const unlock = () => {
    void audio()?.resume().catch(() => undefined);
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

/** One soft note with a quick attack and a natural decay (bell-like with a quiet overtone). */
function note(ac: AudioContext, freq: number, start: number, length: number, volume: number, type: OscillatorType = 'sine') {
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
  gain.connect(ac.destination);
  for (const [mult, share] of [
    [1, 1],
    [2, 0.25],
  ] as const) {
    const osc = ac.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq * mult, start);
    const g = ac.createGain();
    g.gain.value = share;
    osc.connect(g).connect(gain);
    osc.start(start);
    osc.stop(start + length + 0.05);
  }
}

function play(render: (ac: AudioContext, t: number) => void): void {
  try {
    const ac = audio();
    if (!ac || ac.state !== 'running') return;
    render(ac, ac.currentTime + 0.02);
  } catch {
    // sound is a nicety; never let it break the page
  }
}

/** Someone else's turn began: a quiet, short tick. */
export function playTurnTick(): void {
  play((ac, t) => note(ac, 880, t, 0.16, 0.05));
}

/** Your turn: a friendly rising two-note chime. */
export function playYourTurn(): void {
  play((ac, t) => {
    note(ac, 659.25, t, 0.35, 0.12); // E5
    note(ac, 987.77, t + 0.13, 0.55, 0.12); // B5
  });
}

/** A clock tick in the last seconds of your turn (a sharper, higher one for the final three). */
export function playClockTick(urgent = false): void {
  play((ac, t) => {
    // A short filtered noise burst sounds like a mechanical tick, not a beep.
    const length = 0.035;
    const buffer = ac.createBuffer(1, Math.ceil(ac.sampleRate * length), ac.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) samples[i] = (Math.random() * 2 - 1) * (1 - i / samples.length) ** 3;
    const source = ac.createBufferSource();
    source.buffer = buffer;
    const filter = ac.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = urgent ? 3200 : 2200;
    filter.Q.value = 4;
    const gain = ac.createGain();
    gain.gain.value = urgent ? 0.9 : 0.55;
    source.connect(filter).connect(gain).connect(ac.destination);
    source.start(t);
  });
}

/** Tournament winner: a short brass-like fanfare (arpeggio, then a held chord). */
export function playFanfare(): void {
  play((ac, t) => {
    const arp = [523.25, 659.25, 783.99, 1046.5];
    arp.forEach((f, i) => note(ac, f, t + i * 0.12, 0.3, 0.1, 'triangle'));
    const chord = t + arp.length * 0.12 + 0.05;
    for (const f of [523.25, 659.25, 783.99, 1046.5]) note(ac, f, chord, 1.6, 0.07, 'triangle');
    note(ac, 1318.5, chord + 0.25, 1.2, 0.04);
  });
}

/** Sound effects are on unless the player turned them off in the profile. */
export function useSoundSetting(): [boolean, (on: boolean) => Promise<void>] {
  const { me } = useSession();
  const qc = useQueryClient();
  const on = me?.uiPrefs?.sounds !== false;
  const set = async (next: boolean) => {
    qc.setQueryData(['me'], (old: typeof me) => (old ? { ...old, uiPrefs: { ...old.uiPrefs, sounds: next } } : old));
    try {
      await api.patch('/api/me', { uiPrefs: { sounds: next } });
    } catch {
      // not saved: the refetch below shows the stored setting again
    } finally {
      await qc.invalidateQueries({ queryKey: ['me'] });
    }
  };
  return [on, set];
}
